import { useContext } from 'react';
import { EntitlementsContext } from '@/context/EntitlementsContext';
import type { EntitlementsContextValue, SubscriptionTier } from '@/types/entitlements';

/**
 * Hook to access the full entitlements context
 * Use this when you need access to all entitlement data
 */
export function useEntitlements(): EntitlementsContextValue {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error('useEntitlements must be used within an EntitlementsProvider');
  }
  return context;
}

/**
 * Hook to get the current user's subscription tier
 * Returns 'free' for unauthenticated users or users without a subscription
 */
export function useTier(): SubscriptionTier {
  const { tier } = useEntitlements();
  return tier;
}

/**
 * Hook to get the current user's credit balance
 * Returns 0 for unauthenticated users
 */
export function useCredits(): number {
  const { credits } = useEntitlements();
  return credits;
}

/**
 * Hook to check if user can view images based on their entitlements
 */
export function useCanViewImages(): boolean {
  const { flags } = useEntitlements();
  return flags.canViewImages;
}

/**
 * Hook to check if user can view videos based on their entitlements
 */
export function useCanViewVideos(): boolean {
  const { flags } = useEntitlements();
  return flags.canViewVideos;
}

/**
 * Hook to check if a user has at least a specific tier
 * Tier hierarchy (SOW-aligned): free < premium < vip
 */
export function useHasMinTier(minTier: SubscriptionTier): boolean {
  const { tier } = useEntitlements();

  const tierOrder: Record<SubscriptionTier, number> = {
    free: 0,
    premium: 1,
    vip: 2,
  };

  return tierOrder[tier] >= tierOrder[minTier];
}

/**
 * Hook to check if the user's subscription is active
 */
export function useIsSubscriptionActive(): boolean {
  const { subscription } = useEntitlements();
  return subscription?.status === 'active' || subscription?.status === 'trialing';
}

/**
 * Hook to check if entitlements are still loading
 */
export function useEntitlementsLoading(): boolean {
  const { isLoading } = useEntitlements();
  return isLoading;
}
